import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { Subscription } from 'rxjs';

import { SelectOption } from '@fcom/common';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';

export interface PageIndexes {
  startIndex: number;
  endIndex: number;
}

@Component({
  selector: 'fcom-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent implements OnInit, OnDestroy {
  @Input()
  totalItems: number;

  @Input()
  textBeforePages: string;

  @Input()
  itemsPerPageOptions?: number[] = [4, 8, 16];

  @Output()
  indexesChange = new EventEmitter<PageIndexes>();

  options: SelectOption[] = [];
  totalPages: number;
  currentPage = 1;
  itemsPerPage: number;
  startIndex: number;
  endIndex: number;
  reactiveForm: UntypedFormGroup;

  private subscriptions: Subscription = new Subscription();
  readonly FIRST_PAGE = 1;

  constructor(private fb: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.options = this.itemsPerPageOptions.map((value) => ({
      name: `${value}`,
      value: `${value}`,
    }));

    this.createForm();

    this.itemsPerPage = this.itemsPerPageOptions[0];
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    this.setIndexes(this.FIRST_PAGE, true);

    this.subscriptions.add(
      this.reactiveForm.get('paginationCount').valueChanges.subscribe((value) => {
        this.itemsPerPage = +value;
        this.totalPages = Math.round(this.totalItems / this.itemsPerPage);
        this.setIndexes(this.FIRST_PAGE);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  private createForm(): void {
    this.reactiveForm = this.fb.group({
      paginationCount: [this.options[0].value],
    });
  }

  prevPagination(): void {
    this.changePage(this.currentPage - 1);
  }

  nextPagination(): void {
    this.changePage(this.currentPage + 1);
  }

  private changePage(newPageNumber: number): void {
    if (newPageNumber < this.FIRST_PAGE) {
      newPageNumber = this.FIRST_PAGE;
    } else if (newPageNumber > this.totalPages) {
      newPageNumber = this.totalPages;
    }

    this.currentPage = newPageNumber;
    this.setIndexes(newPageNumber);
  }

  private setIndexes(currentPage: number, initial = false): void {
    this.startIndex = (currentPage - 1) * this.itemsPerPage;
    this.endIndex = this.startIndex + this.itemsPerPage;
    if (!initial) {
      this.indexesChange.emit({
        startIndex: this.startIndex,
        endIndex: this.endIndex,
      });
    }
  }
}
